import React from "react";
import { Modal } from "antd";
import { useParams } from "react-router-dom";
import {
  delete_company_file,
  get_company_file,
  get_company_files,
} from "../services/apis/companies";
import { useAppDispatch, useAppSelector } from "../store";
import { IUploadCompanyFilesResponseData } from "../store/types/companies";
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import { real_date } from "../services/convert/date";
import EditUploadDocument from "../pages/company/misc/EditUploadDocument";
import DataTable from "./element/table";
import { ColumnDef } from "@tanstack/react-table";

const DocumentList = () => {
  const { id } = useParams();
  const companyId = Number(id);
  const dispatch = useAppDispatch();
  const pageSize = 10;
  const [pagination, setPagination] = React.useState({
    companyId,
    offset: 0,
  });
  const offset =
    pagination.companyId === companyId ? pagination.offset : 0;
  const {
    documents,
    filesTotal,
    loading,
    selectedFileLoading,
    deletingFileId,
  } = useAppSelector(({ companies }) => ({
    documents: companies.filesCompanyId === companyId ? companies.files : [],
    filesTotal:
      companies.filesCompanyId === companyId ? companies.filesTotal : 0,
    loading: companies.filesCompanyId === companyId && companies.filesLoading,
    selectedFileLoading: companies.selectedFileLoading,
    deletingFileId: companies.deletingFileId,
  }));
  const [view, viewSet] = React.useState<{ show: boolean; path: string }>({
    show: false,
    path: "",
  });
  const [editDocument, setEditDocument] =
    React.useState<IUploadCompanyFilesResponseData | null>(null);

  React.useEffect(() => {
    if (Number.isInteger(companyId) && companyId > 0) {
      get_company_files(dispatch, companyId, pageSize, offset);
    }
  }, [companyId, dispatch, offset]);

  const onEdit = async (documentId: number) => {
    const document = await get_company_file(dispatch, companyId, documentId);
    if (document) setEditDocument(document);
  };

  const onDelete = async (documentId: number) => {
    const deleted = await delete_company_file(dispatch, companyId, documentId);
    if (deleted && documents.length === 1 && offset > 0) {
      setPagination({ companyId, offset: Math.max(0, offset - pageSize) });
    }
  };

  const columns: ColumnDef<IUploadCompanyFilesResponseData>[] = [
    {
      accessorKey: "name",
      header: "Document Name",
    },
    {
      accessorKey: "issuing_date",
      header: "Issue Date",
      cell: ({ getValue }) => {
        const date = getValue<string | Date>();
        return date ? real_date(date) : "—";
      },
    },
    {
      accessorKey: "expiring_date",
      header: "Expiry Date",
      cell: ({ getValue }) => {
        const date = getValue<string | Date>();
        return date ? real_date(date) : "—";
      },
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <div className="w-full flex items-center justify-center gap-2">
          <FaEye
            size={19}
            onClick={() =>
              viewSet({ show: true, path: row.original.document_file })
            }
            className="cursor-pointer"
          />
          <FaEdit
            size={19}
            onClick={() => onEdit(row.original.id)}
            className={`cursor-pointer ${selectedFileLoading ? "pointer-events-none opacity-50" : ""}`}
          />
          <FaTrash
            size={19}
            onClick={() => onDelete(row.original.id)}
            className={`text-red-500 cursor-pointer ${deletingFileId === row.original.id ? "pointer-events-none opacity-50" : ""}`}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      <DataTable
        columns={columns}
        data={documents}
        hasPaginate
        current_page={Math.floor(offset / pageSize) + 1}
        last_page={Math.max(1, Math.ceil(filesTotal / pageSize))}
        onNext={(page) =>
          setPagination({ companyId, offset: (page - 1) * pageSize })
        }
        onPrev={(page) =>
          setPagination({ companyId, offset: (page - 1) * pageSize })
        }
      />
      {loading && (
        <p className="mt-2 text-center text-sm text-gray-500">
          Loading company files...
        </p>
      )}

      {view.path && (
        <Modal
          title="View File"
          onCancel={() => viewSet({ show: false, path: "" })}
          open={view.show}
          footer={false}
          width="90%"
          bodyStyle={{ height: "90vh" }}
          centered={true}
        >
          <iframe title="ppt" className="w-100 h-100" src={view.path} />
        </Modal>
      )}

      {editDocument && (
        <EditUploadDocument
          visible={!!editDocument}
          onHandler={() => {
            setEditDocument(null);
            get_company_files(dispatch, companyId, pageSize, offset);
          }}
          document={editDocument}
        />
      )}
    </div>
  );
};

export default DocumentList;
